import React, { useEffect, useState } from "react";
import { http } from "../src/services/http/api/rest";
import _ from "lodash";
import toastify from "./common/toastify";
import generateMessage from "./services/http/api/message";
import { triggerRefresh, logout } from "./services/tokenService";
import "./styles/component/loadingSpinner.scss";


let isRefreshing = false;
let isSessionExpired = false;

let failedQueue: Array<{
    resolve: () => void;
    reject: (error?: any) => void;
}> = [];


const processQueue = (error: any = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });

    failedQueue = [];
};

const ApiInterceptor = () => {
    const [activeRequests, setActiveRequests] = useState(0);
    const loading=activeRequests>0;


    const startLoading = () => {
        setActiveRequests(prev => prev + 1);
    };

    const stopLoading = () => {
        setActiveRequests(prev => Math.max(0, prev - 1));
    };

    const handleSessionExpired = () => {
        if (isSessionExpired) return;

        isSessionExpired = true;
        logout();
    };


    useEffect(() => {

        const requestInterceptor = http.interceptors.request.use(
            (config: any) => {
                startLoading();
                return config;
            },
            (error: any) => {
                stopLoading();
                return Promise.reject(error);
            }
        );


        const responseInterceptor = http.interceptors.response.use(
            (response: any) => {

                stopLoading();
                return response;
            },


            async (error: any) => {
                stopLoading();

                const originalRequest = error.config;
                const status = error.response?.status;


                // Network failure: server did not return an HTTP response
                if (!error.response) {
                    const isNetworkError =
                        error.code === "ERR_NETWORK" ||
                        error.message === "Network Error" ||
                        !navigator.onLine;

                    if (isNetworkError) {
                        window.dispatchEvent(new Event("network-error"));
                    }

                    return Promise.reject(error);
                }



                const isRefreshRequest =
                    originalRequest?.url?.includes("refresh-token");

                // A refresh request must never trigger another refresh.
                // Let triggerRefresh()'s caller handle its failure.
                if (isRefreshRequest) {
                    return Promise.reject(error);
                }

                if (status !== 401) {
                    await logError(error);
                    return Promise.reject(error);
                }

                // The retried protected request also failed authentication.
                if (originalRequest?._retry) {
                    handleSessionExpired();
                    return Promise.reject(error);
                }

                originalRequest._retry = true;

                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({
                            resolve: () => resolve(http(originalRequest)),
                            reject
                        });
                    });
                }

                isRefreshing = true;

                try {
                    await triggerRefresh();

                    processQueue(null);

                    return http(originalRequest);
                } catch (err: any) {
                    processQueue(err);

                    const refreshStatus = err?.response?.status;

                    // These indicate that the session cannot be refreshed.
                    // Also handle missing/locally expired refresh tokens,
                    // which may throw without an HTTP response.
                    if (
                        [400, 401, 403].includes(refreshStatus) ||
                        err?.message === "No valid token found"
                    ) {
                        handleSessionExpired();
                    }

                    return Promise.reject(err);
                } finally {
                    isRefreshing = false;
                }
            }

        );


        return () => {
            http.interceptors.request.eject(requestInterceptor);
            http.interceptors.response.eject(responseInterceptor);
        };

    }, []);


    const logError = async (error: any) => {
        const status = _.get(error, "response.status");
        const message = _.get(error, "response.data.message", "");
        const subErrors = _.get(error, "response.data.subErrors", []);

        // Validation errors
        if (status === 400 && subErrors.length > 0) {
            subErrors.forEach((err: any) => {
                toastify({
                    title: "Validation Error",
                    message: err.message,
                    isSuccessful: false
                });
            });

            return;
        }

        if (!message) {
            const {title, message: errorMessage} = await generateMessage(status);

            toastify({
                title,
                message: errorMessage,
                isSuccessful: false
            });

            return;
        }

        if (status === 500) {
            toastify({
                title: "Error",
                message: "Internal Server Error",
                isSuccessful: false
            });

            return;
        }

        toastify({
            title: "Error",
            message,
            isSuccessful: false
        });
    };


    return null;
};

export default ApiInterceptor;
